// src/components/chatUnreadListener.js
//
// On init: fetches per-project unread counts from backend (HTTP baseline)
// so badges are correct immediately after page refresh.
// Real-time increments via WebSocket chatNewMessage from there on.

import webSocketService from './WebSocketService';
import { incrementUnread, seedCounts, getOpenProjectId } from './chatUnreadStore';

// ── Sound ────────────────────────────────────────────────
// Browser autoplay policy blocks audio until user interacts with the page.
// Solution:
//   1. Pre-create Audio objects so they are ready to play instantly.
//   2. On first user interaction (click/keydown), call audio.play().pause()
//      to "unlock" each Audio object — browser then allows future plays.
//   3. _audioUnlocked flag prevents re-unlocking on every interaction.

let _mentionAudio, _notificationAudio, _audioUnlocked = false;

function _buildAudio(src) {
  if (!src) return null;
  try {
    return new Audio(typeof src === 'string' ? src : src.default || src);
  } catch { return null; }
}

function _unlockAudio() {
  if (_audioUnlocked) return;
  _audioUnlocked = true;
  [_mentionAudio, _notificationAudio].forEach(audio => {
    if (!audio) return;
    // play+pause immediately unlocks the audio context for this element
    const p = audio.play();
    if (p && p.then) p.then(() => audio.pause()).catch(() => {});
  });
}

// Attach unlock listeners once — removed after first trigger
function _attachUnlockListeners() {
  const handler = () => {
    _unlockAudio();
    document.removeEventListener('click',   handler);
    document.removeEventListener('keydown', handler);
    document.removeEventListener('touchend',handler);
  };
  document.addEventListener('click',    handler, { once: true });
  document.addEventListener('keydown',  handler, { once: true });
  document.addEventListener('touchend', handler, { once: true });
}

try {
  const mentionSrc      = require('../assets/audio/mention.mp3');
  const notificationSrc = require('../assets/audio/notification.mp3');
  _mentionAudio      = _buildAudio(mentionSrc);
  _notificationAudio = _buildAudio(notificationSrc);
  _attachUnlockListeners();
} catch { /* audio files not found — silent */ }

const playSound = (audio, volume = 0.5) => {
  if (!audio) return;
  try {
    audio.volume  = volume;
    audio.currentTime = 0;   // rewind so rapid messages each play from start
    audio.play().catch(() => {});
  } catch { /* ignore */ }
};

const API_BASE = 'https://api.eilandigital.com/eilanApplication-0.0.1-SNAPSHOT/api/project-chat';

let _initialised   = false;
let _unsubNewMsg   = null;
let _currentUserId = null;

// ── messageId dedup ───────────────────────────────────────
const _seen     = new Set();
const _MAX_SEEN = 200;
function _alreadySeen(msgId) {
  if (!msgId) return false;
  const key = String(msgId);
  if (_seen.has(key)) return true;
  _seen.add(key);
  if (_seen.size > _MAX_SEEN) _seen.delete(_seen.values().next().value);
  return false;
}

// ── helpers ───────────────────────────────────────────────
function resolveToken() {
  try {
    const raw = localStorage.getItem('sessionData');
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj?.token && typeof obj.token === 'string') return obj.token;
    }
  } catch { /* ignore */ }
  try {
    const raw = localStorage.getItem('token');
    if (raw) {
      if (!raw.startsWith('{')) return raw;
      const obj = JSON.parse(raw);
      if (obj?.token && typeof obj.token === 'string') return obj.token;
    }
  } catch { /* ignore */ }
  return null;
}

// ── HTTP BASELINE — called on init ────────────────────────
// Fetches per-project unread counts from backend and seeds the store.
// This is what makes badges survive page refresh.
async function loadUnreadBaseline(employeeId) {
  try {
    const res = await fetch(`${API_BASE}/unread/counts/${employeeId}`);
    const json = await res.json();
    if (json.code === 200 && Array.isArray(json.data)) {
      // Shape from backend: [{ project_id, unread_count, has_mention }]
      const baseline = {};
      json.data.forEach(row => {
        const pid = String(row.project_id || row.projectId);
        if (!pid || pid === 'null') return;
        baseline[pid] = {
          count:      Number(row.unread_count  || row.unreadCount  || 0),
          hasMention: Boolean(row.has_mention  || row.hasMention   || false),
        };
      });
      if (Object.keys(baseline).length > 0) {
        seedCounts(baseline);
        console.log('chatUnreadListener: seeded baseline', baseline);
      }
    }
  } catch (err) {
    console.warn('chatUnreadListener: baseline fetch failed (non-fatal):', err.message);
  }
}

// ── INIT ──────────────────────────────────────────────────
export async function initChatUnreadListener(userId) {
  const uid = userId ? String(userId) : null;
  if (!uid) { console.warn('chatUnreadListener: no userId — skipping'); return; }
  if (_initialised && _currentUserId === uid) { console.log('chatUnreadListener: already running for', uid); return; }
  if (_initialised) destroyChatUnreadListener();

  _initialised   = true;
  _currentUserId = uid;
  _seen.clear();

  // 1. Acquire consumer slot
  if (typeof webSocketService.acquire === 'function') webSocketService.acquire();

  // 2. Connect WebSocket
  try {
    await webSocketService.connect(uid, resolveToken());
    console.log('chatUnreadListener: WS connected for', uid);
  } catch (err) {
    console.warn('chatUnreadListener: connect failed (non-fatal):', err.message);
  }

  // 3. HTTP baseline — seed store from backend so refresh shows correct badges
  await loadUnreadBaseline(uid);

  // 4. Register ONE real-time listener
  if (_unsubNewMsg) { console.warn('chatUnreadListener: subscription already exists'); return; }

  _unsubNewMsg = webSocketService.on('chatNewMessage', (data) => {
    if (!data) return;

    const msgId = data?.message?.messageId ?? data?.messageId ?? null;
    if (_alreadySeen(msgId)) return;

    const projectId   = String(data.projectId || data?.message?.projectId || '');
    if (!projectId || projectId === 'undefined' || projectId === 'null') return;

    const senderId    = String(data?.message?.senderId ?? data?.senderId ?? '');
    const currentUser = String(_currentUserId);
    if (senderId && senderId === currentUser) return;

    const mentions   = data?.message?.mentions || data?.mentions || [];
    const hasMention = Array.isArray(mentions) && mentions.some(m => String(m.mentionedEmployeeId) === currentUser);

    // Always play sound for receiver — drawer open or closed
    if (hasMention) playSound(_mentionAudio, 0.7);
    else playSound(_notificationAudio, 0.3);

    // Only increment badge when drawer is NOT open for this project
    if (getOpenProjectId() === projectId) return;

    incrementUnread(projectId, hasMention);
    console.log('chatUnreadListener: +1 project', projectId, '| mention:', hasMention);
  });

  console.log('chatUnreadListener: ready for userId:', uid);
}

// ── DESTROY ───────────────────────────────────────────────
export function destroyChatUnreadListener() {
  if (_unsubNewMsg) { _unsubNewMsg(); _unsubNewMsg = null; }
  if (_initialised && typeof webSocketService.release === 'function') webSocketService.release();
  _initialised   = false;
  _currentUserId = null;
  _seen.clear();
  console.log('chatUnreadListener: destroyed');
}